import React from "react";
import type { ApolloClient } from "@apollo/client";
import { toast } from "react-toastify";
import { isEmpty, get, some } from "lodash";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { connect, type ConnectedProps } from "react-redux";
import { Card } from "react-bootstrap";
import { FiAlertTriangle } from "react-icons/fi";
import { isEmployerSelector } from "store/selectors/user";
import {
  getPayrollHistory,
  cancelPayrollsByBankId,
} from "actions/payrollActions";
import { deleteEmployerBank } from "actions/bankActions";
import { wrapError } from "utils/errorHelper";

import Button from "components/Button";
import Alert from "components/Alert";
import IconCircle from "components/IconCircle";
import IconHeader from "components/IconHeader";
import SimpleModal from "components/SimpleModal";
import type { RootState } from "types/state";

// Shape of the payroll history records read off the GraphQL response via
// lodash `get(data, "data.payrollHistory", [])`.
interface PayrollHistoryItem {
  status?: string;
  bankId?: string;
}

const mapStateToProps = (state: RootState) => {
  return {
    isEmployer: isEmployerSelector(state),
  };
};

const mapDispatchToProps = {
  deleteEmployerBank,
  getPayrollHistory,
  cancelPayrollsByBankId,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  // BankAccounts passes `isExpiredBank` (string | null) here; null is the
  // runtime value when the bank isn't expired — widened to match the JS.
  bankId?: string | null;
  onCancel?: () => void;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  isDeleting: boolean;
  isRemoving: boolean;
  error: string;
  showApprovedModal?: boolean;
  showProcessingModal?: boolean;
}

class RemoveBank extends React.Component<Props, State> {
  _removeInFlight = false;
  _exited = false;

  constructor(props: Props) {
    super(props);

    this.state = { isDeleting: false, isRemoving: false, error: "" };
  }

  _removeBankLink = () => {
    const bankId = this.props.bankId;
    if (this.props.isEmployer) {
      if (this._removeInFlight) {
        return;
      }
      this._removeInFlight = true;
      this._exited = false;
      this.setState({ isRemoving: true, error: "" });
      void this.props
        .getPayrollHistory(this.props.client as ApolloClient<object>)
        .then((data) => {
          if (this._exited) {
            return Promise.resolve();
          }
          const historyError = (data as { error?: unknown } | undefined)?.error;
          if (historyError) {
            this.setState({ error: wrapError(historyError) });
            return Promise.resolve();
          }
          const payrollHistory = get(
            data,
            "data.payrollHistory",
            []
          ) as PayrollHistoryItem[];
          const approved = payrollHistory.filter(
            (payroll) => payroll.status === "APPROVED"
          );
          const processing = payrollHistory.filter(
            (payroll) => payroll.status === "PROCESSING"
          );
          if (
            // @ts-expect-error dead second arg: _isBankInList takes one param;
            // the extra `bankId` is ignored at runtime — preserved from the JS.
            this._isBankInList(approved, bankId)
          ) {
            return this.setState({
              showApprovedModal: true,
            });
            // @ts-expect-error dead second arg: see note above — preserved.
          } else if (this._isBankInList(processing, bankId)) {
            return this.setState({
              showProcessingModal: true,
            });
          } else {
            return this._deleteEmployerBank().then((result) => {
              const error = (result as { error?: unknown } | undefined)?.error;
              if (error) {
                this.setState({ error: wrapError(error) });
                toast.error("There was a problem removing bank account");
                return;
              }
              toast.success("Bank account removed successfully");
            });
          }
        })
        .finally(() => {
          this._removeInFlight = false;
          this.setState({ isRemoving: false });
        });

      return;
    }
  };

  // Declared with a single parameter, yet the call sites in `_removeBankLink`
  // pass a dead second `bankId` arg (the body ignores it and reads
  // `this.props.bankId`). Declaration kept verbatim from the original JS; the
  // extra call-site arg is suppressed there.
  _isBankInList = (payrolls: PayrollHistoryItem[]) => {
    const bankId = this.props.bankId;
    return some(payrolls, (payroll) => payroll.bankId === bankId);
  };

  _deleteEmployerBank = (cancelPayrolls = false) => {
    this.setState({
      showApprovedModal: false,
      showProcessingModal: false,
    });

    const bankId = this.props.bankId;

    if (!cancelPayrolls) {
      return this.props.deleteEmployerBank(
        this.props.client as ApolloClient<object>,
        bankId as string
      );
    }
    return this.props
      .cancelPayrollsByBankId(
        this.props.client as ApolloClient<object>,
        bankId as string
      )
      .then((data) => {
        // `data` is the dispatched action; the FAILURE action carries `error`
        // at runtime, so it is read off a widened view here — preserved guard
        // from the original JS.
        if (data && (data as { error?: unknown }).error) {
          // Propagate the cancel FAILURE action down the chain so the next
          // .then sees the error and shows the error toast — returning
          // Promise.resolve() here would hand it undefined and it would
          // wrongly report success. The guard above proved `error` is present,
          // so narrow to the FAILURE-action member for the .then union.
          return data as { type: string; error: string; success: false };
        }
        return this.props.deleteEmployerBank(
          this.props.client as ApolloClient<object>,
          bankId as string
        );
      })
      .then((data) => {
        // See note above — read the runtime FAILURE `error` field off a widened
        // view of the dispatched action.
        const error = (data as { error?: unknown } | undefined)?.error;
        if (error) {
          this.setState({ error: wrapError(error) });
          toast.error("There was a problem removing bank account");
          return Promise.resolve();
        }
        toast.success("Bank account removed successfully");
      });
  };

  _confirmApprovedPayrollRemoval = () => {
    if (this._removeInFlight) {
      return;
    }
    this._removeInFlight = true;
    this.setState({ isRemoving: true, error: "" });
    void this._deleteEmployerBank(true).finally(() => {
      this._removeInFlight = false;
      this.setState({ isRemoving: false });
    });
  };

  _exitDeleteView = () => {
    this._exited = true;
    if (this.props.onCancel) {
      this.props.onCancel();
    } else {
      this.setState({ isDeleting: false });
    }
  };

  render() {
    const header = "Are you sure you want to remove your bank account?";
    return (
      <>
        <SimpleModal
          // @ts-expect-error showApprovedModal is undefined until first set;
          // passing the optional value verbatim preserves the original JS
          // (react-bootstrap Modal treats an undefined `show` as hidden).
          show={this.state.showApprovedModal}
          onClose={() =>
            this.setState({ showApprovedModal: false }, this._exitDeleteView)
          }
          onSubmit={this._confirmApprovedPayrollRemoval}
          isFetching={this.state.isRemoving}
          title="Payroll Contributions are Approved"
          buttonText="Continue"
        >
          <p>
            {"There are currently payroll contributions approved for processing. By continuing and removing this bank," +
              " the payroll contributions will be un-approved. You will need to re-approve them after adding a new bank" +
              " and associating it to the Icon Plan. Would you like to continue?"}
          </p>
        </SimpleModal>
        <SimpleModal
          // @ts-expect-error showProcessingModal is undefined until first set;
          // passing the optional value verbatim preserves the original JS
          // (react-bootstrap Modal treats an undefined `show` as hidden).
          show={this.state.showProcessingModal}
          onClose={() =>
            this.setState({ showProcessingModal: false }, this._exitDeleteView)
          }
          title="Payroll Contributions are Processing"
          buttonText="Continue"
        >
          <p>
            {"There are currently payroll contributions processing and you are unable to remove the bank account." +
              " Come back in 1 business day, after contributions have processed, to remove the bank."}
          </p>
        </SimpleModal>
        <div>
          <Card>
            <Card.Body>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <IconCircle
                  type="error"
                  icon={
                    <FiAlertTriangle color="white" stroke="#B12121" size={16} />
                  }
                />
              </div>
              <IconHeader variant="centeredText" headerText={header} />

              {!isEmpty(this.state.error) && (
                <Alert type="error" msg={this.state.error} />
              )}

              <div className="remove-bank-action">
                <div className="bank-cancel">
                  <Button
                    size="sm"
                    color="cancel"
                    name="cancel"
                    btnLabel="Cancel"
                    onClick={this._exitDeleteView}
                  />
                </div>
                <Button
                  size="sm"
                  color="red"
                  name="submit"
                  btnLabel="Remove"
                  onClick={() => this._removeBankLink()}
                  withArrow={true}
                  loading={this.state.isRemoving}
                />
              </div>
            </Card.Body>
          </Card>
        </div>
      </>
    );
  }
}

export default connector(withApollo<Props>(RemoveBank));
